/**
 * Instant trade: one-click presets (P1–P3). Each preset holds four order sizes (USD position size),
 * the leverage, optional TP/SL as price % from entry, and the size used by the watchlist L/S buttons.
 * Validation is shared so the API and the editor agree on what's allowed.
 */
import { D } from './decimal.js';
import { floorToStep } from './trading-math.js';
/** Close buttons in the panel, as fractions of the open position on the instrument. */
export const INSTANT_CLOSE_FRACTIONS = ['0.25', '0.5', '1'];
const ceilToStep = (x, step) => x.div(step, 'ceil').round(0, 'ceil').mul(step);
const roundToStep = (x, step) => x.div(step).round(0).mul(step);
/**
 * The smallest order the instrument takes, in whole lot steps: its minimum volume, or the volume worth
 * its minimum order value when that is more. `lotDp`: decimals to show lots with (the lot step's).
 */
export function instantMinOrder(r) {
    const lotValue = D.from(r.lotValue);
    const step = D.from(r.lotStep);
    const lots = ceilToStep(D.max(r.minLots, D.from(r.minNotional).div(lotValue, 'ceil')), step);
    return { lots, value: lots.mul(lotValue).round(2), lotDp: Math.max(0, (step.toString().split('.')[1] ?? '').length) };
}
/**
 * The orders a preset's USD amounts open on an instrument, in whole lot steps like an order in USD
 * (0.05 lots, never 0.0536). When an amount buys less than the minimum (the minimum volume, or the
 * volume worth the minimum order value), the whole row starts at the minimum and keeps the preset's
 * proportions: $100 / $250 / $500 / $1K on BTC at 83,450 opens 0.01 / 0.03 / 0.05 / 0.10 lots.
 */
export function instantSizes(amounts, r) {
    const lotValue = D.from(r.lotValue);
    const step = D.from(r.lotStep);
    const { lotDp, ...min } = instantMinOrder(r);
    const own = amounts.map((a) => floorToStep(D.from(a).div(lotValue, 'down'), step));
    const below = own.map((l) => l.lt(min.lots));
    const fitted = below.some(Boolean);
    const smallest = amounts.reduce((m, a) => (m && m.lte(a) ? m : D.from(a)), null);
    const sizes = own.map((ownLots, i) => {
        const lots = fitted && smallest ? D.max(min.lots, roundToStep(min.lots.mul(amounts[i]).div(smallest), step)) : ownLots;
        const value = lots.mul(lotValue).round(2);
        return { lots, value, overMax: r.maxNotional !== null && value.gt(r.maxNotional) };
    });
    return { fitted, min, below, sizes, lotDp };
}
export const DEFAULT_INSTANT_PREFS = {
    enabled: false,
    active: 0,
    hotkeys: false,
    presets: [
        { amounts: ['100', '250', '500', '1000'], quick: '250', leverage: 20, tpPct: null, slPct: null },
        { amounts: ['250', '500', '1000', '2500'], quick: '500', leverage: 10, tpPct: '2', slPct: '1' },
        { amounts: ['50', '100', '200', '500'], quick: '100', leverage: 5, tpPct: '5', slPct: '2.5' },
    ],
};
const MAX_AMOUNT = D.from('10000000');
function amount(x, label) {
    const s = typeof x === 'number' ? String(x) : x;
    if (typeof s !== 'string' || !D.isValid(s))
        throw new Error(`${label} must be a number`);
    const v = D.from(s);
    if (!v.isPos() || v.gt(MAX_AMOUNT))
        throw new Error(`${label} must be above 0 and at most 10,000,000`);
    return v.toString();
}
function pct(x, label) {
    if (x === null || x === undefined || x === '')
        return null;
    const s = typeof x === 'number' ? String(x) : x;
    if (typeof s !== 'string' || !D.isValid(s))
        throw new Error(`${label} must be a number`);
    const v = D.from(s);
    if (!v.isPos() || v.gt(1000))
        throw new Error(`${label} must be above 0 and at most 1000 %`);
    return v.toString();
}
/** Validate and normalise prefs from a client. Throws Error with a readable message. */
export function parseInstantPrefs(x) {
    if (!x || typeof x !== 'object')
        throw new Error('Preferences must be an object');
    const o = x;
    if (!Array.isArray(o.presets) || o.presets.length !== 3)
        throw new Error('Exactly three presets are required');
    const active = Number(o.active);
    if (active !== 0 && active !== 1 && active !== 2)
        throw new Error('active must be 0, 1 or 2');
    const presets = o.presets.map((raw, i) => {
        const p = (raw ?? {});
        const name = `P${i + 1}`;
        if (!Array.isArray(p.amounts) || p.amounts.length !== 4)
            throw new Error(`${name} needs four amounts`);
        const lev = Number(p.leverage);
        if (!Number.isInteger(lev) || lev < 1 || lev > 1000)
            throw new Error(`${name} leverage must be a whole number from 1 to 1000`);
        return {
            amounts: p.amounts.map((a, j) => amount(a, `${name} amount ${j + 1}`)),
            quick: amount(p.quick, `${name} watchlist amount`),
            leverage: lev,
            tpPct: pct(p.tpPct, `${name} take profit`),
            slPct: pct(p.slPct, `${name} stop loss`),
        };
    });
    return { enabled: o.enabled === true, active, hotkeys: o.hotkeys === true, presets };
}
//# sourceMappingURL=instant-trade.js.map